import { CANVAS_WIDTH } from "../../game/constants";
import { getSettingsPageCount, getSettingsPageDefinition } from "../../game/settingsPages";
import { clamp01, easeOutCubic } from "../../shared/easing";
import type { Rect, UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import { drawAboutContent } from "./drawAbout";
import { drawButton } from "../ui";
import {
  GAME_FONT,
  mFont,
  mFontPlain,
  menuFullButtonW,
  roundedRectPath,
  MENU_PANEL_FILL,
  MENU_TEXT_PRIMARY,
  MENU_TEXT_SECONDARY,
  resolveSettingsLayout,
  resolveOpponentVisibilityLabel,
  truncName,
} from "../renderConstants";

// ── Types ────────────────────────────────────────────────────────────────

export interface SettingsDeps {
  ctx: CanvasRenderingContext2D;
  drawBtn: (rect: Rect, label: string, options?: Parameters<typeof drawButton>[3]) => void;
  drawMenuPageNavButton: (
    rect: Rect,
    label: string,
    style: { fill: string; stroke: string },
  ) => void;
  settingsSlideOffset: () => number;
  staggerAlpha: (index: number, delayMs: number, durationMs: number) => number;
  aboutHeadLogoImage: HTMLImageElement | null;
  aboutWordmarkLogoImage: HTMLImageElement | null;
}

// ── Extracted screen function ────────────────────────────────────────────

export function drawSettingsScreen(deps: SettingsDeps, model: RenderModel): UiRects {
  const ctx = deps.ctx;
  const centerX = CANVAS_WIDTH / 2;
  const page = model.settingsPage;
  const isAdmin = model.canUseDebugSettings;
  const totalPages = getSettingsPageCount({ surface: "main", canUseDebugSettings: isAdmin });
  const pageDef = getSettingsPageDefinition({ surface: "main", canUseDebugSettings: isAdmin }, page);
  const layout = resolveSettingsLayout(centerX, page, isAdmin);
  const hiddenRect: Rect = { x: -1000, y: -1000, w: 1, h: 1 };

  // Full-width card: label left, value right, tap entire card
  const drawSettingsCard = (
    rect: Rect,
    label: string,
    value: string,
    accentColor = "#7ca8ea"
  ): Rect => {
    roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, 6);
    ctx.fillStyle = MENU_PANEL_FILL;
    ctx.fill();
    roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, 6);
    ctx.strokeStyle = accentColor;
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = MENU_TEXT_PRIMARY;
    ctx.font = mFont("bold", 6);
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(label, rect.x + 10, rect.y + rect.h / 2 + 1);

    ctx.fillStyle = accentColor;
    ctx.font = mFont("bold", 6);
    ctx.textAlign = "right";
    ctx.fillText(truncName(value, 14), rect.x + rect.w - 10, rect.y + rect.h / 2 + 1);

    return rect;
  };

  // Full-width slider card: label+% on top, slider track on bottom
  const drawSliderCard = (
    rect: Rect,
    label: string,
    volume: number,
    trackStroke: string,
    fillColor: string,
    labelColor: string
  ): Rect => {
    const clamped = clamp01(volume);

    roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, 6);
    ctx.fillStyle = MENU_PANEL_FILL;
    ctx.fill();
    roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, 6);
    ctx.strokeStyle = trackStroke;
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = MENU_TEXT_PRIMARY;
    ctx.font = mFont("bold", 6);
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(label, rect.x + 10, rect.y + 14);

    ctx.fillStyle = labelColor;
    ctx.textAlign = "right";
    ctx.fillText(`${Math.round(clamped * 100)}%`, rect.x + rect.w - 10, rect.y + 14);

    const trackX = rect.x + 10;
    const trackY = rect.y + 30;
    const trackW = rect.w - 20;
    const trackH = 10;
    const radius = trackH / 2;
    const progressW = Math.max(0, trackW * clamped);
    const knobX = trackX + progressW;

    roundedRectPath(ctx, trackX, trackY, trackW, trackH, radius);
    ctx.fillStyle = "rgba(25,38,62,0.95)";
    ctx.fill();
    roundedRectPath(ctx, trackX, trackY, trackW, trackH, radius);
    ctx.strokeStyle = trackStroke;
    ctx.stroke();

    if (progressW > 0) {
      roundedRectPath(ctx, trackX, trackY, progressW, trackH, radius);
      ctx.fillStyle = fillColor;
      ctx.fill();
    }

    ctx.beginPath();
    ctx.arc(knobX, trackY + trackH / 2, trackH * 0.58, 0, Math.PI * 2);
    ctx.fillStyle = "#e8f7ff";
    ctx.fill();
    ctx.strokeStyle = trackStroke;
    ctx.stroke();

    return { x: trackX, y: trackY - 2, w: trackW, h: trackH + 4 };
  };

  // Title
  ctx.fillStyle = MENU_TEXT_PRIMARY;
  ctx.font = `bold 11px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText(pageDef.mainHeading, centerX, 132);

  // Subtitle
  if (pageDef.mainSubtitle.length > 0) {
    ctx.fillStyle = MENU_TEXT_SECONDARY;
    ctx.font = mFontPlain(6);
    ctx.fillText(pageDef.mainSubtitle, centerX, 150);
  }

  const uiRects: UiRects = {};

  // Page slide + row stagger entrance
  const slideX = deps.settingsSlideOffset();
  if (slideX !== 0) {
    ctx.save();
    ctx.translate(slideX, 0);
    // Fade based on distance from target
    ctx.globalAlpha = (ctx.globalAlpha ?? 1) * (1 - Math.abs(slideX) / 60);
  }

  /** Wraps drawSettingsCard with stagger-entrance alpha. */
  const drawCardStagger = (
    rect: Rect, label: string, value: string, index: number, accentColor?: string
  ): Rect => {
    const alpha = deps.staggerAlpha(index, 40, 160);
    if (alpha <= 0) return drawSettingsCard(rect, label, value, accentColor);
    if (alpha < 1) {
      ctx.save();
      ctx.globalAlpha = (ctx.globalAlpha ?? 1) * easeOutCubic(alpha);
      ctx.translate(0, 12 * (1 - easeOutCubic(alpha)));
    }
    const result = drawSettingsCard(rect, label, value, accentColor);
    if (alpha < 1) ctx.restore();
    return result;
  };

  /** Wraps drawSliderCard with stagger-entrance alpha. */
  const drawSliderStagger = (
    rect: Rect, label: string, vol: number, index: number,
    ts: string, fc: string, lc: string
  ): Rect => {
    const alpha = deps.staggerAlpha(index, 40, 160);
    if (alpha < 1 && alpha > 0) {
      ctx.save();
      ctx.globalAlpha = (ctx.globalAlpha ?? 1) * easeOutCubic(alpha);
      ctx.translate(0, 12 * (1 - easeOutCubic(alpha)));
    }
    const result = drawSliderCard(rect, label, vol, ts, fc, lc);
    if (alpha < 1 && alpha > 0) ctx.restore();
    return result;
  };

  if (pageDef.key === "sound") {
    // Page 0: Sound & Feel — full-width cards with breathing room
    const r = layout.rows;

    uiRects.settingsMusic = drawCardStagger(
      r[0], "MUSIC", model.musicEnabled ? "ON" : "OFF", 0,
      model.musicEnabled ? "#6bff6b" : "#7ca8ea"
    );

    uiRects.musicVolume = drawSliderStagger(
      r[1], "MUSIC VOLUME", model.musicVolume, 1,
      "rgba(124,168,234,0.8)", "rgba(0,242,255,0.75)", "#7a9cc7"
    );

    uiRects.sfxVolume = drawSliderStagger(
      r[2], "SFX VOLUME", model.sfxVolume, 2,
      "rgba(234,168,124,0.8)", "rgba(255,180,0,0.75)", "#c79a7a"
    );

    uiRects.settingsHaptics = drawCardStagger(
      r[3], "HAPTICS", model.hapticsEnabled ? "ON" : "OFF", 3,
      model.hapticsEnabled ? "#6bff6b" : "#7ca8ea"
    );

    uiRects.settingsPreset = drawCardStagger(r[4], "PRESET", model.performance.preset, 4, "#9ec2ff");

    // Hidden rects for off-page controls
    uiRects.settingsSfx = hiddenRect;
    uiRects.settingsSideFx = hiddenRect;
    uiRects.settingsSideFxDensity = hiddenRect;
    uiRects.settingsBgStyle = hiddenRect;
    uiRects.settingsBgBloom = hiddenRect;
    uiRects.settingsBgGlow = hiddenRect;
    uiRects.settingsBgSaturation = hiddenRect;
    uiRects.settingsBgMix = hiddenRect;
    uiRects.settingsBgMotion = hiddenRect;
    uiRects.settingsParticles = hiddenRect;
    uiRects.settingsOpponent = hiddenRect;
    uiRects.settingsFpsCap = hiddenRect;
  } else if (pageDef.key === "background") {
    // Page 1: Background — 5 compact cards
    const r = layout.rows;

    uiRects.settingsBgStyle = drawCardStagger(r[0], "BG STYLE", model.performance.backgroundStyleMode, 0, "#9ec2ff");
    const sideFxValue = model.performance.sideFxMode === "OFF" ? "OFF (BLACK)" : model.performance.sideFxMode;
    uiRects.settingsSideFx = drawCardStagger(r[1], "SIDE FX", sideFxValue, 1, "#64d8ff");
    uiRects.settingsSideFxDensity = drawCardStagger(r[2], "SIDE FX DENSITY", model.performance.sideFxDensityMode, 2, "#64d8ff");
    uiRects.settingsBgBloom = drawCardStagger(r[3], "BLOOM", model.performance.backgroundBloomMode, 3, "#9ec2ff");
    uiRects.settingsBgGlow = drawCardStagger(r[4], "GLOW", model.performance.backgroundGlowMode, 4, "#9ec2ff");

    // Hidden rects for off-page controls
    uiRects.settingsMusic = hiddenRect;
    uiRects.settingsSfx = hiddenRect;
    uiRects.settingsHaptics = hiddenRect;
    uiRects.settingsPreset = hiddenRect;
    uiRects.settingsBgSaturation = hiddenRect;
    uiRects.settingsBgMix = hiddenRect;
    uiRects.settingsBgMotion = hiddenRect;
    uiRects.settingsParticles = hiddenRect;
    uiRects.settingsOpponent = hiddenRect;
    uiRects.settingsFpsCap = hiddenRect;
  } else if (pageDef.key === "effects") {
    // Page 2: Effects — 5 compact cards
    const r = layout.rows;

    uiRects.settingsBgSaturation = drawCardStagger(r[0], "SATURATION", model.performance.backgroundSaturationMode, 0, "#9ec2ff");
    uiRects.settingsBgMix = drawCardStagger(r[1], "VISUAL MIX", model.performance.backgroundMixMode, 1, "#9ec2ff");
    uiRects.settingsBgMotion = drawCardStagger(r[2], "MOTION", model.performance.backgroundMotionMode, 2, "#9ec2ff");
    uiRects.settingsParticles = drawCardStagger(r[3], "PARTICLES", model.performance.particleMode, 3, "#9ec2ff");
    uiRects.settingsOpponent = drawCardStagger(
      r[4],
      "OPPONENT",
      resolveOpponentVisibilityLabel(model.performance.opponentRenderMode, false),
      4,
      "#9ec2ff"
    );

    // Hidden rects for off-page controls
    uiRects.settingsMusic = hiddenRect;
    uiRects.settingsSfx = hiddenRect;
    uiRects.settingsHaptics = hiddenRect;
    uiRects.settingsPreset = hiddenRect;
    uiRects.settingsBgStyle = hiddenRect;
    uiRects.settingsSideFx = hiddenRect;
    uiRects.settingsSideFxDensity = hiddenRect;
    uiRects.settingsBgBloom = hiddenRect;
    uiRects.settingsBgGlow = hiddenRect;
    uiRects.settingsFpsCap = hiddenRect;
  } else if (pageDef.key === "debug") {
    // Page 3: Debug — compact full-width cards
    const r = layout.rows;

    uiRects.settingsDebugEnabled = drawCardStagger(
      r[0], "DEBUG MODE", model.debugSettings.enabled ? "ON" : "OFF", 0,
      model.debugSettings.enabled ? "#6bff6b" : "#7ca8ea"
    );
    uiRects.settingsDebugOverlayGameplay = drawCardStagger(
      r[1], "OVERLAY GAMEPLAY", model.debugSettings.overlayGameplay ? "ON" : "OFF", 1,
      "#7ca8ea"
    );
    uiRects.settingsDebugOverlayBot = drawCardStagger(
      r[2], "OVERLAY BOT", model.debugSettings.overlayBot ? "ON" : "OFF", 2,
      "#7ca8ea"
    );
    uiRects.settingsDebugVerbosity = drawCardStagger(
      r[3], "VERBOSITY", model.debugSettings.verbosity, 3, "#7ca8ea"
    );
    uiRects.settingsDebugDiagnostics = drawCardStagger(
      r[4], "DIAGNOSTICS", model.debugSettings.diagnostics, 4, "#7ca8ea"
    );
    uiRects.settingsDebugPerfSafe = drawCardStagger(
      r[5], "PERF SAFE MODE", model.debugSettings.perfSafeMode ? "ON" : "OFF", 5,
      model.debugSettings.perfSafeMode ? "#6bff6b" : "#7ca8ea"
    );
    uiRects.debugResetOnboarding = drawCardStagger(
      r[6], "RESET ONBOARDING", "RESET", 6, "#ff9966"
    );

    // Hidden rects for off-page controls
    uiRects.settingsMusic = hiddenRect;
    uiRects.settingsSfx = hiddenRect;
    uiRects.settingsHaptics = hiddenRect;
    uiRects.settingsSideFx = hiddenRect;
    uiRects.settingsSideFxDensity = hiddenRect;
    uiRects.settingsBgStyle = hiddenRect;
    uiRects.settingsBgBloom = hiddenRect;
    uiRects.settingsBgGlow = hiddenRect;
    uiRects.settingsBgSaturation = hiddenRect;
    uiRects.settingsBgMix = hiddenRect;
    uiRects.settingsBgMotion = hiddenRect;
    uiRects.settingsPreset = hiddenRect;
    uiRects.settingsParticles = hiddenRect;
    uiRects.settingsOpponent = hiddenRect;
    uiRects.settingsFpsCap = hiddenRect;
  } else if (pageDef.key === "about") {
    drawAboutContent(
      {
        ctx,
        aboutHeadLogoImage: deps.aboutHeadLogoImage,
        aboutWordmarkLogoImage: deps.aboutWordmarkLogoImage,
      },
      {
        centerX,
        topY: 190,
        maxWidth: menuFullButtonW() - 20,
        bodyFontPx: 7,
        bodyLineHeight: 15,
        logoGapY: 18,
        logoInlineGap: 12,
        headMaxHeight: 52,
        wordmarkMaxHeight: 38,
        fallbackFontPx: 7,
      },
    );

    uiRects.settingsMusic = hiddenRect;
    uiRects.settingsSfx = hiddenRect;
    uiRects.settingsHaptics = hiddenRect;
    uiRects.settingsPreset = hiddenRect;
    uiRects.settingsSideFx = hiddenRect;
    uiRects.settingsSideFxDensity = hiddenRect;
    uiRects.settingsBgStyle = hiddenRect;
    uiRects.settingsBgBloom = hiddenRect;
    uiRects.settingsBgGlow = hiddenRect;
    uiRects.settingsBgSaturation = hiddenRect;
    uiRects.settingsBgMix = hiddenRect;
    uiRects.settingsBgMotion = hiddenRect;
    uiRects.settingsParticles = hiddenRect;
    uiRects.settingsOpponent = hiddenRect;
    uiRects.settingsFpsCap = hiddenRect;
    uiRects.settingsDebugEnabled = hiddenRect;
    uiRects.settingsDebugOverlayGameplay = hiddenRect;
    uiRects.settingsDebugOverlayBot = hiddenRect;
    uiRects.settingsDebugVerbosity = hiddenRect;
    uiRects.settingsDebugDiagnostics = hiddenRect;
    uiRects.settingsDebugPerfSafe = hiddenRect;
    uiRects.debugResetOnboarding = hiddenRect;
  }

  // Close the page-slide transform (cards only, footer stays fixed)
  if (slideX !== 0) {
    ctx.restore();
  }

  // Detect if opened from gameplay
  const pre = model.preSettingsPhase;
  const fromGameplay = pre != null
    && (pre === "RUN_PLAYABLE" || pre === "RUN_RESOLVING" || pre === "RUN_LEVELUP" || pre === "VERSUS_MATCH");

  // Footer: [PREV] [BACK/RESUME] [NEXT/EXIT]
  const prevStyle = page > 0
    ? { fill: "#1f2e44", stroke: "#6f94c5" }
    : { fill: "#151b2a", stroke: "#3a4a6a" };
  const nextStyle = page < totalPages - 1
    ? { fill: "#1f2e44", stroke: "#6f94c5" }
    : { fill: "#151b2a", stroke: "#3a4a6a" };

  if (fromGameplay) {
    // Pause menu: just RESUME + EXIT, no page arrows
    const pauseResumeRect: Rect = { x: layout.prev.x, y: layout.back.y, w: (layout.next.x + layout.next.w - layout.prev.x - 4) / 2, h: layout.back.h };
    const pauseExitRect: Rect = { x: pauseResumeRect.x + pauseResumeRect.w + 4, y: layout.back.y, w: pauseResumeRect.w, h: layout.back.h };
    deps.drawBtn(pauseResumeRect, "RESUME", {
      fill: "#1f3d2a",
      stroke: "#75cc80",
      font: mFont("bold", 7)
    });
    uiRects.settingsBack = pauseResumeRect;
    deps.drawBtn(pauseExitRect, "EXIT", {
      fill: "#3d1f1f",
      stroke: "#cc7575",
      font: mFont("bold", 7)
    });
    uiRects.mainMenu = pauseExitRect;
  } else {
    deps.drawMenuPageNavButton(layout.prev, "\u25C4", prevStyle);
    uiRects.settingsBgPrev = layout.prev;
    deps.drawBtn(layout.back, "BACK", {
      fill: "#2a1f3d",
      stroke: "#9975cc",
      font: mFont("bold", 6)
    });
    uiRects.settingsBack = layout.back;
    deps.drawMenuPageNavButton(layout.next, "\u25BA", nextStyle);
    uiRects.settingsBgNext = layout.next;
  }

  return uiRects;
}
